import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SearchInput } from "@src/components/SearchInput";
import { useForm } from "react-hook-form";
import { theme } from "@src/theme";
import MultiSelect from "@src/components/MultiSelect";
import { ITag } from "@src/types/tags";
import { useFetchTagsInfiniteQuery } from "@src/queries/tagQueries";
import { useDebounce } from "@src/hooks/useDebounce";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { UIButton } from "@src/components/UIButton";
import UIModal from "@src/components/UIModal/UIModal";
import { ITrack } from "@src/types/track";

type UpdateTrackRegularTagsModalProps = {
  onConfirm: (tagIds: string[]) => void;
  onClose: () => void;
  isOpen: boolean;
  loading?: boolean;
  track: ITrack | null;
};

const UpdateTrackRegularTagsModal: React.FC<
  UpdateTrackRegularTagsModalProps
> = ({ onConfirm, onClose, isOpen, loading, track }) => {
  const [selectedTags, setSelectedTags] = useState<ITag[]>([]);
  const trackTagsIdsRef = useRef<string[] | null>(null);
  const { register, watch } = useForm({
    defaultValues: { searchText: "" },
  });
  const searchText = useDebounce<string>(watch("searchText"), 600);

  const {
    data: tagsResponse,
    isLoading: isTagsLoading,
    isError: isTagsError,
    error: tagsError,
    isFetching: isTagsFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchTagsInfiniteQuery({
    searchText,
    enabled: isOpen, // Only fetch when modal is open
  });

  const memorizedTags = useMemo(() => {
    if (!tagsResponse?.pages || !Array.isArray(tagsResponse.pages)) {
      return [];
    }
    const allTags =
      (tagsResponse.pages as AxiosResponse<ApiResponse<ITag[]>>[]).flatMap(
        (response) => {
          if (!response?.data?.data || !Array.isArray(response.data.data)) {
            return [];
          }
          return response.data.data;
        }
      ) || [];
    return allTags;
  }, [tagsResponse]);

  const optionsEndRef = useRef<HTMLDivElement | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    if (!isOpen) return;

    let observer: IntersectionObserver | null = null;

    const timeoutId = setTimeout(() => {
      if (!optionsEndRef.current) return;

      observer = new IntersectionObserver(handleObserver, {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      });

      const currentRef = optionsEndRef.current;
      observer.observe(currentRef);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observer && optionsEndRef.current) {
        observer.unobserve(optionsEndRef.current);
      }
    };
  }, [isOpen, handleObserver, searchText, memorizedTags.length]);

  // Initialize selection only when modal opens or track changes
  useEffect(() => {
    if (!isOpen) {
      trackTagsIdsRef.current = null;
      setSelectedTags([]);
      return;
    }

    const trackTags = track && Array.isArray(track.tags) ? track.tags : [];
    const currentTrackTagIds = trackTags
      .map((tag: ITag) => tag.documentId)
      .sort();

    // Only initialize if track tags changed or it's the first time opening
    const currentIdsString = currentTrackTagIds.join(",");
    const previousIdsString = trackTagsIdsRef.current?.join(",") || "";

    if (currentIdsString !== previousIdsString) {
      trackTagsIdsRef.current = currentTrackTagIds;

      if (trackTags.length > 0 && memorizedTags.length > 0) {
        const normalizedTags = trackTags
          .map((tag) => {
            return memorizedTags.find(
              (mTag) => mTag.documentId === tag.documentId
            );
          })
          .filter((tag): tag is ITag => tag !== undefined);
        // Use a small delay to ensure the selector updates properly
        const timeoutId = setTimeout(() => {
          setSelectedTags(normalizedTags);
        }, 100);
        return () => clearTimeout(timeoutId);
      } else {
        const timeoutId = setTimeout(() => {
          setSelectedTags([]);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [track?.tags, isOpen]);

  // If tags become available after initial load and we have track tags but no selection, find them
  useEffect(() => {
    if (!isOpen) return;

    const trackTags = track && Array.isArray(track.tags) ? track.tags : [];

    // Only try to find tags if we have track tag IDs, no current selection, and tags are available
    if (
      trackTags.length > 0 &&
      selectedTags.length === 0 &&
      memorizedTags.length > 0 &&
      trackTagsIdsRef.current &&
      trackTagsIdsRef.current.length > 0
    ) {
      const normalizedTags = trackTags
        .map((tag) => {
          return memorizedTags.find(
            (mTag) => mTag.documentId === tag.documentId
          );
        })
        .filter((tag): tag is ITag => tag !== undefined);
      if (normalizedTags.length > 0) {
        const timeoutId = setTimeout(() => {
          setSelectedTags(normalizedTags);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [memorizedTags, isOpen, track?.tags, selectedTags.length]);

  const checkIfTagsChanged = () => {
    if (track?.tags && Array.isArray(track.tags)) {
      const currentTagIds = track.tags.map((tag: ITag) => tag.documentId);
      const selectedTagIds = selectedTags.map((tag) => tag.documentId);
      return (
        currentTagIds.length !== selectedTagIds.length ||
        currentTagIds.some((id: string) => !selectedTagIds.includes(id))
      );
    }
    return selectedTags.length > 0;
  };

  const onCancelSubmit = () => {
    onClose();
    if (track?.tags && memorizedTags.length > 0) {
      const tags = Array.isArray(track.tags) ? track.tags : [];
      const normalizedTags = tags
        .map((tag) => {
          return memorizedTags.find(
            (mTag) => mTag.documentId === tag.documentId
          );
        })
        .filter((tag): tag is ITag => tag !== undefined);
      setSelectedTags(normalizedTags);
    } else {
      setSelectedTags([]);
    }
  };

  const handleConfirm = () => {
    const selectedTagIds = selectedTags.map((tag) => tag.documentId);
    onConfirm(selectedTagIds);
  };

  return (
    <UIModal
      modalStyles={{
        width: 600,
      }}
      onClose={onClose}
      isOpen={isOpen}
      actions={[
        <UIButton
          key="cancel"
          onClick={onCancelSubmit}
          variant="secondaryOutline"
        >
          Cancel
        </UIButton>,
        <UIButton
          key="submit"
          onClick={handleConfirm}
          variant="primary"
          style={{ minWidth: 100 }}
          disabled={
            isTagsLoading || isTagsFetching || loading || !checkIfTagsChanged()
          }
        >
          {loading ? "Updating..." : "Submit"}
        </UIButton>,
      ]}
      headerStyles={{
        textAlign: "center",
      }}
      title="Tags"
    >
      <div style={{ padding: theme.margins.medium }}>
        <div
          style={{
            paddingBottom: theme.margins.medium,
          }}
        >
          <SearchInput
            placeholder="Search by tag name"
            {...register("searchText")}
          />
        </div>
        {isTagsError ? (
          <div>Error loading tags: {tagsError?.message}</div>
        ) : (
          <MultiSelect<ITag>
            enableCreate={false}
            optionEntityName="tag"
            options={memorizedTags || []}
            labelKey={"title"}
            valueKey={"documentId"}
            setSelected={setSelectedTags}
            selected={selectedTags}
            menuItems={[]}
            loading={isTagsLoading || isTagsFetching}
            loaderRef={optionsEndRef}
          />
        )}
      </div>
    </UIModal>
  );
};

export default UpdateTrackRegularTagsModal;
